import { auth } from '@/utils/firebase';
import {
	adaptFacetSearchResponse,
	adaptSearchResponse,
	buildTypesenseSearch,
} from './folioSearchAdapter';

const endpoint = process.env.REACT_APP_SEARCH_API;

const postSearches = async (searches) => {
	const token = await auth.currentUser?.getIdToken();
	if (!token) {
		throw new Error('Search requires a signed-in user');
	}

	const response = await fetch(endpoint, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Authorization: token,
		},
		body: JSON.stringify({ searches }),
	});

	if (!response.ok) {
		throw new Error(`Search failed with status ${response.status}`);
	}

	const payload = await response.json();
	if (!Array.isArray(payload?.results) || payload.results.length !== searches.length) {
		throw new Error('Search failed');
	}
	return payload.results;
};

const emptyHitResult = (request) => {
	const params = request?.params ?? {};
	return {
		hits: [],
		nbHits: 0,
		page: params.page ?? 0,
		nbPages: 0,
		hitsPerPage: params.hitsPerPage ?? 20,
		processingTimeMS: 0,
		exhaustiveNbHits: true,
		query: params.query ?? '',
		params: '',
		facets: {},
	};
};

const emptyFacetResult = () => ({
	facetHits: [],
	exhaustiveFacetsCount: true,
	processingTimeMS: 0,
});

/**
 * A rejected search promise is an unhandled error in dev and opens the webpack
 * overlay over the whole page. Return an empty result instead so lists stay usable.
 */
const searchOrEmpty = async (requests, run, emptyResult) => {
	try {
		return await run();
	} catch (err) {
		console.error('Search failed', err);
		return requests.map((request) => emptyResult(request));
	}
};

/**
 * InstantSearch client with the same search / searchForFacetValues shape as
 * typesense-instantsearch-adapter. Requests are translated here and executed
 * by the Folio search proxy, which applies the caller's scope.
 */
export const createFolioSearchClient = ({ additionalSearchParameters = {} } = {}) => {
	const searchClient = {
		clearCache() {
			return searchClient;
		},
		async search(requests) {
			const results = await searchOrEmpty(
				requests,
				async () => {
					const searches = requests.map((request) =>
						buildTypesenseSearch(request, additionalSearchParameters),
					);
					const payload = await postSearches(searches);
					return payload.map((result, index) =>
						adaptSearchResponse(result, requests[index]),
					);
				},
				emptyHitResult,
			);
			return { results };
		},
		async searchForFacetValues(requests) {
			return searchOrEmpty(
				requests,
				async () => {
					const searches = requests.map((request) =>
						buildTypesenseSearch(request, additionalSearchParameters),
					);
					const payload = await postSearches(searches);
					return payload.map((result, index) =>
						adaptFacetSearchResponse(result, requests[index]),
					);
				},
				emptyFacetResult,
			);
		},
	};

	return searchClient;
};
